import { Component, OnInit } from "@angular/core"
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatCardModule } from "@angular/material/card"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { SnackbarService } from "../../services/snackbar.service"
import { Router, RouterLink, RouterModule, ActivatedRoute } from "@angular/router"
import { CognitoService } from "../../services/cognito.service"
import { LoadingSpinnerComponent } from "../../shared/loading-spinner/loading-spinner.component"
import { AuthFloatLabelDirective } from "../../directives/auth-float-label.directive"

@Component({
  selector: "app-set-password",
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    RouterLink,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    AuthFloatLabelDirective,
  ],
  templateUrl: "./set-password.component.html",
  styleUrl: "./set-password.component.css",
})
export class SetPasswordComponent implements OnInit {
  passwordForm: FormGroup
  loading = false
  error = ""
  success = ""
  email = ""
  session = ""
  code = ""
  passwordType: "new" | "reset" = "new"
  showPassword = false
  showConfirmPassword = false

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cognitoService: CognitoService,
    private snackbar: SnackbarService,
  ) {
    this.passwordForm = this.formBuilder.group(
      {
        password: ["", [Validators.required, Validators.minLength(8)]],
        confirmPassword: ["", [Validators.required]],
      },
      { validators: this.passwordMatchValidator },
    )
  }

  ngOnInit() {
    // Get parameters from route
    this.route.queryParams.subscribe((params) => {
      this.email = params["email"] || ""
      this.session = params["session"] || ""
      this.code = params["code"] || ""
      this.passwordType = params["type"] || "new"

      if (!this.email) {
        this.router.navigate(["/login"])
        return
      }
    })
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get("password")
    const confirmPassword = form.get("confirmPassword")

    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true })
      return { passwordMismatch: true }
    }

    return null
  }

  onSubmit() {
    if (this.passwordForm.invalid) {
      return
    }

    this.loading = true
    this.error = ""
    this.success = ""

    const newPassword = this.passwordForm.get("password")?.value

    if (this.passwordType === "reset") {
      // Confirm forgot password with code
      this.cognitoService.confirmForgotPassword(this.email, this.code, newPassword).subscribe({
        next: (response: any) => {
          this.loading = false
          this.success = "Password reset successfully!"
          this.snackbar.success("Password reset successful! You can now log in.", 5000)

          // Redirect to login after a short delay
          setTimeout(() => {
            this.router.navigate(["/login"])
          }, 2000)
        },
        error: (error: any) => {
          this.loading = false
          this.error = error.error?.message || "Failed to reset password. Please try again."
          this.snackbar.error(this.error, 5000)
        },
      })
    } else {
      // Set new password for NEW_PASSWORD_REQUIRED challenge
      this.cognitoService.setNewPassword(this.email, newPassword, this.session).subscribe({
        next: (response: any) => {
          this.loading = false
          this.success = "Password set successfully!"
          this.snackbar.success("Password set successfully! Welcome to Radio Ads Direct.", 5000)

          // Redirect to user home after a short delay
          setTimeout(() => {
            this.router.navigate(["/userhome"])
          }, 2000)
        },
        error: (error: any) => {
          this.loading = false
          this.error = error.error?.message || "Failed to set password. Please try again."
          this.snackbar.error(this.error, 5000)
        },
      })
    }
  }

  getPasswordStrength(password: string): string {
    if (!password) return ""

    let score = 0

    // Length check
    if (password.length >= 8) score++
    if (password.length >= 12) score++

    // Character variety checks
    if (/[a-z]/.test(password)) score++
    if (/[A-Z]/.test(password)) score++
    if (/[0-9]/.test(password)) score++
    if (/[^A-Za-z0-9]/.test(password)) score++

    if (score < 3) return "weak"
    if (score < 5) return "medium"
    return "strong"
  }

  getPasswordStrengthText(strength: string): string {
    switch (strength) {
      case "weak":
        return "Weak - Add more characters and variety"
      case "medium":
        return "Medium - Consider adding special characters"
      case "strong":
        return "Strong - Good password!"
      default:
        return ""
    }
  }

  // Getters for template usage
  get passwordControl() {
    return this.passwordForm.get("password")
  }

  get confirmPasswordControl() {
    return this.passwordForm.get("confirmPassword")
  }

  get passwordStrength() {
    const password = this.passwordControl?.value || ""
    return this.getPasswordStrength(password)
  }

  getPageTitle(): string {
    return this.passwordType === "reset" ? "Reset Your Password" : "Set New Password"
  }

  getPageDescription(): string {
    if (this.passwordType === "reset") {
      return "Enter your new password below."
    } else {
      return "Please set a new password for your account."
    }
  }
}
